import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UploadFile } from 'ngx-uploader';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppGlobals } from '@app/app.global';
import { AuthService, EntityService } from '@app/core';
import { Invoice } from '@modules/invoice/entities';
import { IInvoice, InvoiceStatus } from '@modules/invoice/interfaces';
import { UploadService } from './upload.service';


type Tab = 'uploaded' | 'reviewed' | 'invalid';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  invoicesToBeUploaded$: BehaviorSubject<{ pages: { file: UploadFile, preview?: any }[], progress?: number, subscription?: Subscription, isUploadComplete?: boolean }[]>;
  activeInvoiceGroup$: BehaviorSubject<Tab>;
  invoiceFilters$: BehaviorSubject<{ [key: string]: { [key: string]: string | string[] } }>;
  uploadInvoiceEntity: BehaviorSubject<string>;
  private _entitiesWithPendingInvoices: any[] = [];
  get entitiesWithPendingInvoices(): any[] {
    if (!this._entitiesWithPendingInvoices?.length) {
      try {
        const cached = localStorage.getItem('entitiesWithPendingInvoices');
        if (cached) {
          this._entitiesWithPendingInvoices = JSON.parse(cached);
        }
      } catch (e) {}
    }
    return this._entitiesWithPendingInvoices;
  }
  set entitiesWithPendingInvoices(val: any[]) {
    this._entitiesWithPendingInvoices = val;
    localStorage.setItem('entitiesWithPendingInvoices', JSON.stringify(val || []));
  }

  private _currentInvoiceList: Invoice[] = [];
  get currentInvoiceList(): Invoice[] {
    if (!this._currentInvoiceList?.length) {
      try {
        const cached = localStorage.getItem('currentInvoiceList');
        if (cached) {
          this._currentInvoiceList = JSON.parse(cached).map((i: any) => new Invoice(i));
        }
      } catch (e) {}
    }
    return this._currentInvoiceList;
  }
  set currentInvoiceList(val: Invoice[]) {
    this._currentInvoiceList = val;
    localStorage.setItem('currentInvoiceList', JSON.stringify(val ? val.map(i => i.invoiceProps || i) : []));
  }

  private _currentInvoiceListCount: number = 0;
  get currentInvoiceListCount(): number {
    if (!this._currentInvoiceListCount) {
      const cached = localStorage.getItem('currentInvoiceListCount');
      if (cached) {
        this._currentInvoiceListCount = +cached;
      }
    }
    return this._currentInvoiceListCount;
  }
  set currentInvoiceListCount(val: number) {
    this._currentInvoiceListCount = val;
    localStorage.setItem('currentInvoiceListCount', `${val}`);
  }

  constructor(private http: HttpClient, private appGlobals: AppGlobals, private authService: AuthService, private uploadService: UploadService,
    private entityService: EntityService,
  ) {
    this.invoicesToBeUploaded$ = new BehaviorSubject<{
      pages: { file: UploadFile, preview?: any }[],
      progress?: number, subscription?: Subscription, isUploadComplete?: boolean
    }[]>([]);
    this.uploadInvoiceEntity = new BehaviorSubject('');

    const activeInvoiceGroup = localStorage.getItem('activeInvoiceGroup');
    this.activeInvoiceGroup$ = new BehaviorSubject<Tab>(<Tab>activeInvoiceGroup ?? 'uploaded');
    this.activeInvoiceGroup$.subscribe(activeInvoiceGroup => {
      if (activeInvoiceGroup) {
        localStorage.setItem('activeInvoiceGroup', activeInvoiceGroup);
      }
    });

    const existingInvoiceListFilters = localStorage.getItem('invoiceListFilters');

    this.invoiceFilters$ = new BehaviorSubject<{ [key: string]: { [key: string]: string | string[] } }>(existingInvoiceListFilters ? JSON.parse(existingInvoiceListFilters) : null);
    this.invoiceFilters$.subscribe(filters => {
      if (filters) {
        localStorage.setItem('invoiceListFilters', JSON.stringify(filters));
      }
    });
  }

  // getInvoiceTypes(): Observable<string[]> {
  //   //return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-types`);
  // }

  getInvoice(id: string, options: { ocrSource?: 'gcp' | 'composer' | 'newdata' } = {}) {
    const params: { id: string; ocr_source?: string } = { id };

    if (options.ocrSource) {
      params.ocr_source = options.ocrSource;
    }

    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-detail`, {
      params
    }).pipe(map((invoice: IInvoice) => new Invoice(invoice)));
  }

  /**
   * Who opened New Data. Returns null on failure so callers can avoid marking the session as tracked.
   */
  trackNewDataOpen(body: { invoiceId: string; companyId?: string; sourceOcrResultId?: string }) {
    const payload: { invoiceId: string; companyId?: string; sourceOcrResultId?: string } = {
      invoiceId: body.invoiceId
    };
    if (body.companyId) {
      payload.companyId = body.companyId;
    }
    if (body.sourceOcrResultId) {
      payload.sourceOcrResultId = body.sourceOcrResultId;
    }

    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}invoice-ocr/track-new-data-open`, payload).pipe(
      catchError(err => {
        console.warn('[new-data-tracking] POST failed', err?.status, err?.message || err);
        return of(null);
      })
    );
  }

  getInvoices(filters: HttpParams | { [params: string]: string | string[] } = {}): Observable<{ invoices: Invoice[], statuses: { status: InvoiceStatus, count: number }[], documentTypes: { documentType: string, count: number }[], count: number }> {
    if (filters?.['status'] == InvoiceStatus.ALL) {
      delete filters['status'];
    }

    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-list`, {
      params: filters
    }).pipe(
      map((res: any) => ({ ...res, invoices: res.invoices.map((invoice: IInvoice) => new Invoice(invoice)) }))
    );
  }

  uploadInvoice(files: File[]): Observable<HttpEvent<any>> {
    let formData: FormData = new FormData();

    files.forEach(file => formData.append('file', file, file.name));

    return this.uploadService.uploadFilesToS3(files, this.entityService.getSingleSelectedEntity()?.id);
    // if (this.authService.user.isOwner) {
    //   // return this.http.post<any>(`${this.appGlobals.bypassApiUrl.invoice}upload-invoice`, formData, {
    //   //   reportProgress: true, observe: "events",
    //   //   params: { entity: this.getUploadInvoiceEntity() }
    //   // });
    // } else {
    //   return this.uploadService.uploadFilesToS3(files, this.entityService.getSingleSelectedEntity()?.id);
    //   // return this.http.post<any>(`${this.appGlobals.bypassApiUrl.invoice}upload-invoice`, formData, {
    //   //   reportProgress: true, observe: "events"
    //   // });
    // }
  }

  // Admin upload with specific entity ID
  uploadInvoiceWithEntity(files: File[], entityId: string): Observable<HttpEvent<any>> {
    return this.uploadService.uploadFilesToS3(files, entityId);
  }

  changeStatus(ids: string[], status: ('Active' | 'Inactive' | 'Archieved' | 'Deleted')) {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}change-invoice-statuses`, {
      params: { 'ids[]': ids, status }
    });
  }

  getInvoiceCount(): Observable<{ [key: string]: number; }> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-count`);
  }

  getEntitiesWithPendingInvoices(companyId: string, invoiceStatusGroup?: string): Observable<any[]> {
    let params = {};
    if (invoiceStatusGroup) {
      params = {
        invoiceStatusGroup: invoiceStatusGroup
      };
    }
    return this.http.get<any[]>(`${this.appGlobals.apiUrl.invoice}company/${companyId}/entities-with-pending-invoices`, { params });
  }

  getToBeReviewedInvoices(filters: HttpParams | { [params: string]: string | string[] } = {}) {
    if (filters?.['status'] == InvoiceStatus.ALL) {
      delete filters['status'];
    }

    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-list-to-review`, {
      params: filters
    });
  }

  reviewInvoice(invoice) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoiceV2}review-invoice`, invoice);

  }

  autoSaveReview(invoice) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoiceV2}auto-save-invoice`, invoice);
  }

  saveReviewDuration(id, duration) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}save-review-duration`, { id, duration });
  }

  getRejectionReasons() {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}rejection-reasons`);
  }

  rejectInvoice(id, rejectReasonId, otherReason) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}reject-invoice`, { id, rejectReasonId, otherReason });
  }

  bulkRejectInvoices(ids: string[], rejectReasonId: string, otherReason: string) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}bulk-reject-invoices`, { ids, rejectReasonId, otherReason });
  }

  createQualityIssue(invoiceId: string, issueType: string, remarks?: string) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.metadata}quality-issue`, { invoiceId, issueType, remarks });
  }

  updateQualityIssue(id: string, payload: { issueType?: string, remarks?: string, status?: string }) {
    return this.http.put<any>(`${this.appGlobals.apiUrl.metadata}quality-issue/${id}`, payload);
  }

  deleteQualityIssue(id: string) {
    return this.http.delete<any>(`${this.appGlobals.apiUrl.metadata}quality-issue/${id}`);
  }

  getQualityIssuesByInvoice(invoiceId: string) {
    return this.http.get<any>(`${this.appGlobals.apiUrl.metadata}quality-issue/by-invoice/${invoiceId}`);
  }

  getProductKeys(): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}product-keys`);
  }

  getTotalKeys(): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}total-keys`);
  }

  getOriginalInvoice(invoiceId: string, filters: HttpParams | { [params: string]: string | string[] | boolean } = {}) {
    const isCreditNote = filters['isCreditNote'] === true || filters['isCreditNote'] === 'true';
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}original-invoice`, {
      params: {
        invoiceId,
        ...filters,
        isCreditNote: String(isCreditNote)
      }
    });
  }

  getInvoiceDateByNumber(queryParams): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-date`, {
      params: queryParams
    });
  }

  getInvoiceByDate(queryParams): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}invoice-number`, {
      params: queryParams
    });
  }

  transferInvoiceEntity(body) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}transfer-invoice`, body);
  }

  setUplodInvoiceEntity(entity: string) {
    this.uploadInvoiceEntity.next(entity);
  }

  getUploadInvoiceEntity() {
    return this.uploadInvoiceEntity.value;
  }

  getTdsRates() {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}tds-rates`);
  }

  getTdsNatures() {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}tds-natures`);
  }

  getTcsRates() {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}tcs-rates`);
  }

  getTcsNatures() {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}tcs-natures`);
  }

  getPanDetail(pan: string) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}get-pan-detail`, { pan });
  }

  getGstDetail(gst: string) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}get-gst-detail`, { gst });
  }

  getSellerPan(queryParams): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}seller-pan`, {
      params: queryParams
    });
  }

  getProductDescriptions(queryParams): Observable<any[]> {
    if (!queryParams?.companyName) {
      return of([]);
    }

    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}suggested-product-names`, {
      params: queryParams
    });
  }

  saveActivityLog(activityLog) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}activity-log`, activityLog);
  }

  getActivityLog(queryParams): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}activity-log`, {
      params: queryParams
    });
  }

  addIpTrace(body) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.metadata}ip-tracing`, body);
  }

  getSupplierTags(filters: HttpParams | { [params: string]: string | string[] } = {}) {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}supplier-tags`, { params: filters });
  }

  getCompanyGstPan(company = {}) {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}company-gst-pan`, {
      params: company
    });
  }

  getReviewpageStaticData(): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}reviewpage-static-data`);
  }

  postExtraction(requestBody) {
    return this.http.post<any>(`${this.appGlobals.apiUrl.invoice}ai-prompt-request`, requestBody);
  }

  getAIPromptRequestDetail(batchId) {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}get-ai-prompt-request-details?batchId=${batchId}`);
  }
}